/**
 * Экран «Контроль» — §14.2, подэтап C5.
 *
 * Отвечает на вопрос, который человек задаёт, уже получив сигнал в
 * телефон: **что вообще срабатывало**. Под лентой — по каким правилам:
 * пороги и цели, действующие сейчас.
 *
 * **Правила здесь только видны, а задаются в «Настройках».** Так делит
 * экраны §14.2 («Настройки: таргеты, подписки, пороги»), и так попросил
 * владелец 11.09.2026. До того пороги и цели правились прямо здесь, и одна
 * и та же цифра жила на двух экранах: где её поменяли и какая действует —
 * вопрос, который человеку задавать незачем. Здесь её видно рядом с тем,
 * что по ней сработало; меняют её в одном месте.
 *
 * **Подавленные сигналы показываются наравне** (§16.4). Сигнал, о котором
 * человек не узнал, — тоже событие, и увидеть его он должен здесь,
 * вместе с причиной, по которой его не отправили. Иначе разбирательство
 * «почему нам не сказали» упирается в пустоту.
 *
 * **Текст сигнала приходит с сервера готовым и уже разобранным** —
 * заголовок, срочность и остальной текст. Собирает его тот же код, который
 * рассылает сообщение в бот: две копии одного текста разошлись бы на
 * первой правке. Разбирает тоже сервер: первая строка сообщения —
 * «значок название», и на карточке, где свой заголовок и своя цветная
 * точка, она повторила бы заголовок, второй раз — эмодзи.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@shell/ui';
import { Empty, Loading } from '@shell/states';
import { formatDate, formatMoney } from '@shell/format';
import { financeApi } from '../api';
import type { AlertList, AlertRow, AlertRule, ControlRules, PeriodParams } from '../api';
import { PeriodPicker, ScreenError, Section } from '../components';

/** Срочность словами. Глазом её видно по цвету точки, а экранному диктору
 *  и человеку, не различающему цвета, нужна подпись. */
const MARK_TITLE: Record<string, string> = {
  high: 'срочно',
  medium: 'важно',
  low: 'для сведения',
};

function AlertCard({ alert }: { alert: AlertRow }) {
  const suppressed = alert.status === 'suppressed';
  const level = alert.mark || alert.severity;
  return (
    <article className={suppressed ? 'alert alert--suppressed' : 'alert'}>
      <header className="alert__head">
        <span
          className={`alert__mark alert__mark--${suppressed ? 'suppressed' : level}`}
          role="img"
          aria-label={suppressed ? 'не отправлен' : (MARK_TITLE[level] ?? MARK_TITLE.medium)}
        />
        <strong>{alert.headline || alert.title}</strong>
        {alert.business_date && (
          <span className="alert__day">{formatDate(alert.business_date)}</span>
        )}
      </header>
      {alert.body && <pre className="alert__text">{alert.body}</pre>}
      {suppressed && (
        <p className="alert__reason">
          Не отправлен: {alert.suppressed_reason}. Событие записано — данные были неполны,
          и сигнал по ним ушёл бы неверным.
        </p>
      )}
    </article>
  );
}

function Feed({
  period,
  onPeriodChange,
}: {
  period: PeriodParams;
  onPeriodChange: (next: PeriodParams) => void;
}) {
  const [kind, setKind] = useState<string>('');
  const query = useQuery({
    queryKey: ['finance', 'alerts', period, kind],
    queryFn: () => financeApi.alerts({ ...period, kind: kind || undefined }),
  });

  if (query.isLoading) return <Loading />;
  if (query.error) {
    return (
      <ScreenError
        error={query.error}
        period={period}
        anchor={period.to ?? null}
        onPeriodChange={onPeriodChange}
        onRetry={() => query.refetch()}
      />
    );
  }

  const data = query.data as AlertList;

  return (
    <>
      <PeriodPicker period={period} anchor={period.to ?? null} onChange={onPeriodChange} />

      <div className="filters" role="group" aria-label="Виды сигналов">
        <Button small variant={kind === '' ? 'primary' : 'quiet'} onClick={() => setKind('')}>
          Все
        </Button>
        {data.kinds.map((item) => (
          <Button
            key={item.code}
            small
            variant={kind === item.code ? 'primary' : 'quiet'}
            onClick={() => setKind(item.code)}
          >
            {item.title}
          </Button>
        ))}
      </div>

      {data.alerts.length === 0 ? (
        <Empty
          title="Сигналов не было"
          text="Это хорошая новость, а не поломка: сигнал приходит, когда цифра вышла за норму или в кассе случилось что-то необычное."
        />
      ) : (
        <div className="alerts">
          {data.alerts.map((alert) => (
            <AlertCard key={alert.id} alert={alert} />
          ))}
        </div>
      )}

      {data.suppressed > 0 && (
        <p className="note">
          Подавлено при неполных данных: {data.suppressed}. Такие сигналы не отправлялись,
          но остались здесь — молча не отправленный сигнал неотличим от несработавшего.
        </p>
      )}
    </>
  );
}

/** Порог словами: «больше 5 000 ₽», «больше 3 000 ₽ или 30 % чека». */
function thresholdText(rule: AlertRule): string {
  if (!rule.is_active) return 'выключен';
  const parts: string[] = [];
  if (rule.threshold !== null) parts.push(`больше ${formatMoney(Number(rule.threshold))}`);
  if (rule.threshold_pct !== null) {
    parts.push(`${Number(rule.threshold_pct).toLocaleString('ru-RU')} % чека`);
  }
  return parts.length > 0 ? parts.join(' или ') : 'порог не задан';
}

/**
 * Действующие правила — только чтение. Поле ввода здесь было бы вторым
 * местом правки той же цифры; кнопка ведёт в первое и единственное.
 */
function Rules({
  rules,
  onOpenSettings,
  settingsLabel,
}: {
  rules: ControlRules;
  onOpenSettings: () => void;
  settingsLabel: string;
}) {
  return (
    <>
      <ul className="list">
        {rules.rules.map((rule) => (
          <li key={rule.kind}>
            <strong>{rule.title}</strong> — {thresholdText(rule)}
            {rule.is_default && <span className="muted"> · по умолчанию</span>}
          </li>
        ))}
        {rules.targets.map((target) => (
          <li key={target.metric_code}>
            <strong>{target.title}</strong> — {target.description}
            <span className="muted"> · цель с {formatDate(target.valid_from)}</span>
          </li>
        ))}
      </ul>
      {rules.targets.length === 0 && (
        <p className="note">
          Целей нет: отклонения ищутся по норме — среднему за восемь таких же дней недели.
        </p>
      )}
      <div className="form-row">
        <Button small onClick={onOpenSettings}>
          {settingsLabel}
        </Button>
      </div>
    </>
  );
}

export function Control({
  period,
  onPeriodChange,
  onOpenSettings,
  settingsLabel = 'Изменить в «Настройках»',
}: {
  period: PeriodParams;
  onPeriodChange: (next: PeriodParams) => void;
  onOpenSettings: () => void;
  /** Подпись кнопки правки правил. На портале правила живут не в «Настройках». */
  settingsLabel?: string;
}) {
  const rules = useQuery({
    queryKey: ['finance', 'control'],
    queryFn: financeApi.controlRules,
  });

  return (
    <div className="screen">
      <Section title="Что срабатывало">
        <Feed period={period} onPeriodChange={onPeriodChange} />
      </Section>

      {rules.data && (
        <Section title="По каким правилам">
          <Rules rules={rules.data} onOpenSettings={onOpenSettings} settingsLabel={settingsLabel} />
        </Section>
      )}
    </div>
  );
}
